'use client'

import type { ComponentPropsWithoutRef } from 'react'
import ReactMarkdown from 'react-markdown'
import type { ExtraProps } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils'

// Security-conscious components (reuses patterns from markdown-content.tsx)
const markdownComponents = {
  a: ({
    node: _node,
    ...props
  }: ComponentPropsWithoutRef<'a'> & ExtraProps) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      className="text-status-blue-text hover:underline"
    />
  ),
  img: () => null,
}

interface MarkdownPreviewProps {
  value: string
  className?: string
  minHeight?: string
}

export function MarkdownPreview({
  value,
  className,
  minHeight = '120px',
}: MarkdownPreviewProps) {
  if (!value) {
    return (
      <div
        className={cn('px-3 py-2 text-sm text-text-secondary', className)}
        style={{ minHeight }}
        data-testid="markdown-preview"
      >
        Nothing to preview
      </div>
    )
  }

  // Issue #13159 - sized as a flex item of MarkdownInput's column-flex
  // wrapper, NOT `h-full`: the wrapper's height is indefinite (the container
  // is capped by max-height only), so a percentage height computes to `auto`,
  // the pane grows to its content, and overflow-y-auto never engages.
  return (
    <div
      className={cn(
        'px-3 py-2 flex-1 min-h-0 overflow-y-auto',
        'prose prose-sm dark:prose-invert max-w-none',
        'prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5',
        'prose-headings:mt-3 prose-headings:mb-1.5',
        className,
      )}
      style={{ minHeight }}
      // Focusable so keyboard users can scroll the pane once it overflows
      // (Safari does not make scroll containers keyboard-focusable).
      tabIndex={0}
      role="region"
      aria-label="Markdown preview"
      data-testid="markdown-preview"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {value}
      </ReactMarkdown>
    </div>
  )
}
